import { defineStore } from 'pinia';
import { useApi } from '@lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import type {
  ColorCustomValuesUpdateArgs,
  AddPaletteArgs,
  BrandColorLibraryField,
  BrandColorValues,
  ColorDto,
  ColorImageArg,
  ColorImagesDto,
  ColorLibraryDto,
  ColorPaletteWithColorsDto,
  ColorWithUsageDto,
  SetColorArgs,
  UpdatePaletteArgs,
} from '@/ApiClient';
import { useUser } from '@/store/user';
import { useArticleTable } from '@/store/article-table';
import { useArticleCards } from '@/store/article-cards';
import { useComponents } from '@/store/components';
import type { AxiosRequestConfig } from 'axios';
import { computed, ref } from 'vue';
import { useColorPaletteStatuses } from '@/store/color-palette-statuses';
import { isColorsEqual } from '@lib/colorManagement';
import baseColors from './data/base-colors.json';
import suggestionsColors from './data/suggestions-colors.json';
import { signalrListen } from '@lib/signalr';

/** Which colours a search looks through: everything the brand can reach, or one palette or library. */
export type ColorSearchSource = { paletteId?: number; libraryId?: number };

export const useColors = defineStore('colors', () => {
  const api = useApi();

  //#region Palettes
  const basePalettes = useGlobalStore({
    load: api.color_GetPalettes,
    dto: 'ColorPaletteDto',
    create: (_, args: AddPaletteArgs) => api.color_AddPalette(useUser().brandId, args),
    update: (_, paletteId: number, args: UpdatePaletteArgs) =>
      api.color_UpdatePalette(useUser().brandId, paletteId, args),
    signalR: 'ColorPaletteUpdate',
  });

  /** The palettes the colour pickers offer to search in: those whose status does not hide them. */
  const pickerPalettes = computed(() =>
    basePalettes.loaded.value.filter((palette) => !useColorPaletteStatuses().byId[palette.statusId]?.hideInColorPicker)
  );

  async function getAll(): Promise<ColorPaletteWithColorsDto[]> {
    return await api.color_GetPalettesWithColors(useUser().brandId);
  }

  async function addPalette(name: string, iconName?: string, seasonId?: number) {
    const colorPalette = await api.color_AddPalette(useUser().brandId, { name, icon: iconName, seasonId });

    basePalettes.loaded.value.push(colorPalette);

    return colorPalette;
  }

  async function updatePalette(
    paletteId: number,
    name: string,
    iconName: string | undefined,
    seasonId: number | undefined,
    statusId: number
  ) {
    const colorPalette = await api.color_UpdatePalette(useUser().brandId, paletteId, {
      name,
      icon: iconName,
      seasonId,
      statusId,
    });

    const index = basePalettes.loaded.value.findIndex((i) => i.id === colorPalette.id);
    if (index > -1) {
      basePalettes.loaded.value[index] = colorPalette;
    }

    return colorPalette;
  }

  async function deletePalette(paletteId: number) {
    await api.color_DeletePalette(useUser().brandId, paletteId);
    basePalettes.loaded.value = basePalettes.loaded.value.filter((i) => i.id !== paletteId);
  }

  //#endregion

  //#region Recently used colors
  const recentlyUsed = ref<ColorDto[]>([]);

  async function loadRecentlyUsed() {
    recentlyUsed.value = await api.color_GetRecentlyUsed(useUser().brandId);
    return recentlyUsed;
  }

  async function colorWasRecentlyUsed(color: ColorDto) {
    recentlyUsed.value = recentlyUsed.value.filter((c) => !isColorsEqual(color, c));
    recentlyUsed.value.unshift(color);

    recentlyUsed.value = recentlyUsed.value.slice(0, 10);
  }

  function recentlyUsedListen() {
    signalrListen('NewRecentlyUsedColor', colorWasRecentlyUsed, { listenForever: true });
    signalrListen('ReloadRecentlyUsedColors', loadRecentlyUsed, { listenForever: true });
  }
  //#endregion

  async function loadAll() {
    await basePalettes.loadAll();
    await loadRecentlyUsed();
  }

  function listen() {
    basePalettes.listen();
    recentlyUsedListen();

    // A colour's values are copied into every article, component and BOM payload, so a
    // rename or a merge leaves stale colours all over the SWR caches. dto-hashes only
    // invalidates on a schema change, which this is not — so drop the caches that could
    // be holding the colour and let them revalidate.
    signalrListen(
      'BrandColorUpdate',
      () => {
        useArticleTable().clear();
        useArticleCards().clear();
        useComponents().clear();

        // Dropped so a later page open refetches, and reloaded outright when something is
        // already showing the list: the Colors page renders straight from it, so a change made
        // in another tab — or by a merge, or by an edit from a picker — must reach it without a
        // reload of the page. Colours have no per-id patch to apply; the list carries usage
        // counts that a rename or a merge moves too.
        const wasLoaded = colors.value.length > 0;
        colorsPromise = null;

        if (wasLoaded) void loadColors();
      },
      { listenForever: true }
    );
  }

  async function search(
    searchTerm: string,
    take: number,
    offset = 0,
    source: ColorSearchSource = {},
    options?: AxiosRequestConfig
  ) {
    const colors = await api.color_SearchColors(
      {
        brandId: useUser().brandId,
        searchTerm,
        paletteId: source.paletteId ?? 0,
        libraryId: source.libraryId ?? 0,
        take,
        offset,
      },
      options
    );

    return { colors };
  }

  /**
   * The libraries this brand may search, for the picker's source filter. Loaded on demand and kept:
   * a handful of rows that only change from the admin page.
   */
  const libraries = ref<ColorLibraryDto[]>([]);
  let librariesPromise: Promise<void> | null = null;

  function loadLibraries() {
    librariesPromise ??= (async () => {
      libraries.value = (await api.brandColor_GetLibraries(useUser().brandId)).filter((library) => library.enabled);
    })().catch((e) => {
      librariesPromise = null;
      throw e;
    });

    return librariesPromise;
  }

  //#region The brand's colours
  // Not global data: a brand can have a few thousand colours, so this is loaded on demand by the
  // pages that need the whole list rather than on every app start.
  const colors = ref<ColorWithUsageDto[]>([]);
  let colorsPromise: Promise<void> | null = null;

  async function loadColors(force = false) {
    if (force) colorsPromise = null;
    colorsPromise ??= (async () => {
      colors.value = await api.brandColor_GetColors(useUser().brandId);
    })().catch((e) => {
      colorsPromise = null;
      throw e;
    });
    return colorsPromise;
  }

  /** Shared by every history cell on the page, so they resolve ids from one fetch. */
  const loadColorsForHistory = () => loadColors();

  const colorById = (id: number) => colors.value.find((c) => c.color.id === id)?.color ?? null;

  async function updateColor(colorId: number, values: BrandColorValues) {
    const updated = await api.brandColor_UpdateColor(useUser().brandId, colorId, values);
    await loadColors(true);
    return updated;
  }

  /**
   * Saves several colours in one request. The Colors table writes whole selections and pasted
   * blocks, which cell by cell would be a request and a full list reload each.
   */
  async function updateColors(
    updates: { colorId: number; values: BrandColorValues }[],
    numberUpdates: { colorId: number; number: string | undefined }[] = [],
    groupSequentialNumberUpdates: { colorId: number; groupSequentialNumber: number | undefined }[] = [],
    customValueUpdates: ColorCustomValuesUpdateArgs[] = []
  ) {
    const updated = await api.brandColor_UpdateColors(useUser().brandId, {
      updates,
      numberUpdates,
      groupSequentialNumberUpdates,
      customValueUpdates,
    });
    await loadColors(true);
    return updated;
  }

  /** Hands calculated fields back to their calculation on these colours, dropping the values written over them. */
  async function relinkCustomField(colorIds: number[], fieldId: number) {
    return updateColors(
      [],
      [],
      [],
      colorIds.map((colorId) => ({ colorId, customValues: [{ id: fieldId, value: undefined, linked: true }] }))
    );
  }

  /**
   * Tags and untags palettes across a set of colours. Add/remove rather than a replacement list,
   * so editing a multi-row selection leaves the palettes it says nothing about alone.
   */
  async function setColorPalettes(colorIds: number[], addPaletteIds: number[], removePaletteIds: number[]) {
    const updated = await api.brandColor_SetColorPalettes(useUser().brandId, {
      colorIds,
      addPaletteIds,
      removePaletteIds,
    });
    await loadColors(true);
    return updated;
  }

  /** Puts the colours' images as the server returned them onto the list's rows. */
  function patchImages(updated: ColorImagesDto[]) {
    for (const { colorId, images, imageAsSwatch } of updated) {
      const row = colors.value.find((c) => c.color.id === colorId);
      if (!row) continue;
      row.images = images;
      row.imageAsSwatch = imageAsSwatch;
    }
  }

  /**
   * Sets the images of one or more colours: the pictures listed stay where they are, the ones marked for
   * every colour are added where missing, and the rest are removed. `imageAsSwatch` sets whether the first
   * image stands in for each colour's swatch, or with undefined leaves each colour as it is. Patched into
   * the list rather than reloading it: nothing else on a row moves with its images.
   */
  async function setColorImages(
    colorIds: number[],
    images: ColorImageArg[],
    reorder: boolean,
    imageAsSwatch: boolean | undefined
  ) {
    const updated = await api.brandColor_UpdateImages(useUser().brandId, { colorIds, images, reorder, imageAsSwatch });
    patchImages(updated);
    return updated;
  }

  /** Gives a new version of one colour's image to the other colours' images that showed the same picture. */
  async function spreadColorImageVersion(colorIds: number[], azureBlobFileId: number, replacedUrl: string) {
    const updated = await api.brandColor_SpreadImageVersion(useUser().brandId, {
      colorIds,
      azureBlobFileId,
      replacedUrl,
    });
    patchImages(updated);
    return updated;
  }

  async function setColorStatus(colorIds: number[], statusId: number) {
    const updated = await api.brandColor_SetColorStatus(useUser().brandId, { colorIds, statusId });
    await loadColors(true);
    return updated;
  }

  async function setColorType(colorIds: number[], typeId: number) {
    const updated = await api.brandColor_SetColorType(useUser().brandId, { colorIds, typeId });
    await loadColors(true);
    return updated;
  }

  async function mergeColors(keepId: number, mergeIds: number[]) {
    const kept = await api.brandColor_MergeColors(useUser().brandId, { keepId, mergeIds });
    await loadColors(true);
    return kept;
  }

  async function deleteColors(colorIds: number[]) {
    await api.brandColor_DeleteColors(useUser().brandId, colorIds);
    await loadColors(true);
  }

  /** Puts one of a colour's values back to its library entry's, or all of them when none is named. */
  async function resetColorToLibrary(colorId: number, field?: BrandColorLibraryField) {
    const reset = await api.brandColor_ResetToLibrary(useUser().brandId, colorId, { field });
    await loadColors(true);
    return reset;
  }

  /** Points existing colours at a library colour: they take the library's colour, keeping their own names and codes. */
  async function linkColorsToLibrary(colorIds: number[], libraryColorId: number) {
    const linked = await api.brandColor_LinkToLibrary(useUser().brandId, { colorIds, libraryColorId });
    await loadColors(true);
    return linked;
  }

  /** Cuts colours loose from their library entries, each keeping every value it shows now as its own. */
  async function unlinkColorsFromLibrary(colorIds: number[]) {
    const unlinked = await api.brandColor_UnlinkFromLibrary(useUser().brandId, colorIds);
    await loadColors(true);
    return unlinked;
  }

  /**
   * The enabled libraries' colours alone, for linking a colour the brand already has to one of them.
   * A `libraryId` of 0 searches every enabled library.
   */
  const searchLibraryColors = (
    searchTerm: string,
    libraryId: number,
    take: number,
    offset: number,
    options?: AxiosRequestConfig
  ) => api.brandColor_SearchLibraryColors({ brandId: useUser().brandId, searchTerm, libraryId, take, offset }, options);

  const colorUsage = (colorId: number) => api.brandColor_GetUsage(useUser().brandId, colorId);

  /** The articles a merge keeping `keepId` would refuse to renumber, since their status locks the number. */
  const previewMerge = (keepId: number, mergeIds: number[]) =>
    api.brandColor_PreviewMerge(useUser().brandId, { keepId, mergeIds });

  /** Turns a picker result into one of the brand's colours, by the server's rule for an edited colour. */
  async function resolveColor(args: SetColorArgs) {
    const color = await api.brandColor_ResolveColor(useUser().brandId, args);
    await loadColors(true);
    return color;
  }

  async function adoptLibraryColor(libraryColorId: number, values?: BrandColorValues) {
    const adopted = await api.brandColor_AdoptLibraryColor(useUser().brandId, { libraryColorId, values });
    await loadColors(true);
    return adopted;
  }
  //#endregion

  //#region Palette membership
  async function addColorToPalette(paletteId: number, colorId: number) {
    return await api.color_AddColorToPalette(useUser().brandId, paletteId, colorId);
  }

  async function removeColorFromPalette(paletteId: number, colorId: number) {
    return await api.color_RemoveColorFromPalette(useUser().brandId, paletteId, colorId);
  }

  async function addColorsToPalette(paletteId: number, colors: BrandColorValues[]) {
    return await api.color_AddPaletteColorsToPalette(useUser().brandId, paletteId, { colors });
  }
  //#endregion

  return {
    baseColors,
    suggestionsColors,
    recentlyUsed,
    palettes: basePalettes.loaded,
    pickerPalettes,
    getAll,
    addPalette,
    updatePalette,
    deletePalette,
    loadAll,
    listen,
    search,
    libraries,
    loadLibraries,
    addColorToPalette,
    removeColorFromPalette,
    addColorsToPalette,
    colors,
    loadColors,
    loadColorsForHistory,
    colorById,
    updateColor,
    updateColors,
    relinkCustomField,
    setColorPalettes,
    setColorImages,
    spreadColorImageVersion,
    setColorStatus,
    setColorType,
    mergeColors,
    deleteColors,
    resetColorToLibrary,
    linkColorsToLibrary,
    unlinkColorsFromLibrary,
    searchLibraryColors,
    colorUsage,
    previewMerge,
    adoptLibraryColor,
    resolveColor,
  };
});
